import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useRoutesHelper } from '../../helpers/routesHelper';
import { useCreateDistributorByEmailMutation } from '../../core/api/accountSlice';
import { PagesRoutes } from '../../store/routes';
import {
  ReflinkCard,
  ReflinkCloseButton,
  ReflinkActions,
  ReflinkCtaButtonCompact,
  ReflinkEmailInput,
  ReflinkFrame,
  ReflinkHeroFrame,
  ReflinkHeroImg,
  ReflinkLinkDisplay,
  ReflinkJoinLink,
  REFLINK_STEP_2_SRC,
} from './reflinkShared';

/** Desktop-only: slightly above default `56.2%`; mobile unchanged. */
const Step2PrimaryActions = styled(ReflinkActions)`
  @media (min-width: 641px) {
    && {
      top: 52.5% !important;
    }
  }
`;

const Step2JoinActions = styled(ReflinkActions)`
  @media (min-width: 641px) {
    && {
      bottom: 8.5% !important;
    }
  }
`;

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const ReflinkStep2Page: FC = () => {
  const navigate = useNavigate();
  const { createReferralLink } = useRoutesHelper();
  const [createDistributor, { isLoading }] = useCreateDistributorByEmailMutation();

  const [email, setEmail] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [referralSlug, setReferralSlug] = useState<string>('');
  const [heroReady, setHeroReady] = useState(false);
  const heroImgRef = useRef<HTMLImageElement>(null);

  useLayoutEffect(() => {
    const el = heroImgRef.current;
    if (el?.complete && el.naturalWidth > 0) {
      setHeroReady(true);
    }
  }, []);

  const referralUrl = useMemo(() => {
    if (!referralSlug) return '';
    return createReferralLink(referralSlug);
  }, [createReferralLink, referralSlug]);

  const hasLink = !!referralUrl;
  const canCreate = isValidEmail(email) && !isLoading && !hasLink;
  const canCopy = hasLink && !isLoading;

  const copyToClipboard = async (text: string) => {
    let ok = false;
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        ok = true;
      } catch {
        ok = false;
      }
    }
    if (!ok) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        ok = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch {
        ok = false;
      }
    }
    return ok;
  };

  const handleCreateLink = async () => {
    if (!canCreate) return;
    try {
      const res = await createDistributor({ email: email.trim() }).unwrap();
      if (res?.referralSlug) {
        setReferralSlug(res.referralSlug);
      }
    } catch {
      // user can retry
    }
  };

  const handleCopyLink = async () => {
    if (!canCopy || !referralUrl) return;
    const ok = await copyToClipboard(referralUrl);
    if (ok) {
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 5000);
    }
  };

  const handlePrimaryClick = () => {
    if (hasLink) {
      void handleCopyLink();
    } else {
      void handleCreateLink();
    }
  };

  return (
    <>
      <Helmet>
        <title>Referral link</title>
      </Helmet>
      <motion.div
        initial={false}
        animate={{ opacity: heroReady ? 1 : 0 }}
        transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: '100%', pointerEvents: heroReady ? 'auto' : 'none' }}
      >
        <ReflinkCard>
          <ReflinkFrame>
            <ReflinkCloseButton type="button" aria-label="Close" onClick={() => navigate(PagesRoutes.Main, { replace: true })}>
              ×
            </ReflinkCloseButton>
            <ReflinkHeroFrame>
              <ReflinkHeroImg
                ref={heroImgRef}
                src={REFLINK_STEP_2_SRC}
                alt="Copy referral link"
                onLoad={() => setHeroReady(true)}
              />
            </ReflinkHeroFrame>

            {hasLink ? (
              <ReflinkLinkDisplay title={referralUrl}>
                {referralUrl}
              </ReflinkLinkDisplay>
            ) : (
              <ReflinkEmailInput
                value={email}
                placeholder="you@example.com"
                inputMode="email"
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            )}

            <Step2PrimaryActions style={{ top: '56.2%' }} $mobileTranslateY={'clamp(14px, 2.6vw, 20px)'}>
              <ReflinkCtaButtonCompact type="button" disabled={hasLink ? !canCopy : !canCreate} onClick={handlePrimaryClick}>
                {isLoading
                  ? 'Please wait...'
                  : hasLink
                    ? isCopied
                      ? 'Copied'
                      : 'Copy link'
                    : 'Get link'}
              </ReflinkCtaButtonCompact>
            </Step2PrimaryActions>

            <Step2JoinActions style={{ bottom: '6.4%' }}>
              <ReflinkJoinLink type="button" onClick={() => navigate(PagesRoutes.Main)}>
                Join
              </ReflinkJoinLink>
            </Step2JoinActions>
          </ReflinkFrame>
        </ReflinkCard>
      </motion.div>
    </>
  );
};

export default ReflinkStep2Page;

